import { useLayoutEffect } from 'react'

const EASE = 'cubic-bezier(.16,.84,.24,1)'
const UP = [
  { opacity: 0, transform: 'translateY(16px)' },
  { opacity: 1, transform: 'none' },
]

/** 시안 motion.js의 line · reveal · fade · stagger · swap · bob · ba · progress. 스크롤 기준은 .renewal-root. count는 최종값을 그대로 둔다. */
export function useRenewalMotion() {
  useLayoutEffect(() => {
    const root = document.querySelector('.renewal-root')
    if (!root) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const seen = new WeakSet<Element>()
    const stops: Array<() => void> = []

    const visible = (el: HTMLElement) => {
      const rect = el.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) return false
      return rect.top < window.innerHeight * 0.94
    }

    const whenVisible = (el: HTMLElement, play: () => void) => {
      if (visible(el)) {
        play()
        return
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting || entry.boundingClientRect.top < 0)) {
            io.disconnect()
            root.removeEventListener('scroll', onScroll)
            play()
          }
        },
        { root, rootMargin: '0px 0px -6% 0px' }
      )
      const onScroll = () => {
        if (!visible(el)) return
        io.disconnect()
        root.removeEventListener('scroll', onScroll)
        play()
      }
      io.observe(el)
      root.addEventListener('scroll', onScroll, { passive: true })
      stops.push(() => {
        io.disconnect()
        root.removeEventListener('scroll', onScroll)
      })
    }

    const enter = (el: HTMLElement, frames: Keyframe[], duration: number, delay: number) => {
      const animation = el.animate(frames, { duration, delay, easing: EASE, fill: 'both' })
      animation.pause()
      animation.onfinish = () => animation.cancel()
      return animation
    }

    const delayOf = (el: HTMLElement) => {
      const value = Number(el.getAttribute('data-delay'))
      return Math.min(Number.isFinite(value) ? value : 0, 360)
    }

    const setup = (el: HTMLElement) => {
      if (seen.has(el)) return
      const kind = el.getAttribute('data-motion')
      if (kind === 'ba') {
        seen.add(el)
        const top = el.querySelector('[data-ba-top]') as HTMLElement | null
        const handle = el.querySelector('[data-ba-handle]') as HTMLElement | null
        const range = el.querySelector('input[type="range"]') as HTMLInputElement | null
        if (!top || !range) return
        let current = 50
        let touched = 0
        let visible = true
        let held = false
        let alive = true
        const started = Date.now()
        const setX = (value: number) => {
          current = value
          top.style.clipPath = `inset(0 0 0 ${value}%)`
          if (handle) handle.style.left = `${value}%`
        }
        const poke = () => {
          touched = Date.now()
        }
        const onInput = () => {
          poke()
          setX(Number(range.value))
        }
        const hold = () => {
          held = true
        }
        const release = () => {
          held = false
          touched = 0
        }
        range.addEventListener('input', onInput)
        range.addEventListener('pointerdown', poke)
        range.addEventListener('touchstart', poke, { passive: true })
        range.addEventListener('focus', hold)
        range.addEventListener('blur', release)
        el.addEventListener('mouseenter', hold)
        el.addEventListener('mouseleave', release)
        setX(Number(range.value) || 50)
        let observer: IntersectionObserver | undefined
        if (!reduce) {
          if ('IntersectionObserver' in window) {
            observer = new IntersectionObserver((entries) => {
              visible = entries[0].isIntersecting
            })
            observer.observe(el)
          }
          const loop = () => {
            if (!alive || !el.isConnected) return
            if (visible && !held && Date.now() - touched > 3000) {
              const cycle = ((Date.now() - started) % 6400) / 6400
              const target = 50 + 45 * Math.sin(cycle * 2 * Math.PI)
              const value = current + (target - current) * 0.12
              setX(Number(value.toFixed(2)))
              range.value = String(value)
            }
            window.setTimeout(loop, 33)
          }
          window.setTimeout(loop, 33)
        }
        stops.push(() => {
          alive = false
          observer?.disconnect()
          range.removeEventListener('input', onInput)
          range.removeEventListener('pointerdown', poke)
          range.removeEventListener('touchstart', poke)
          range.removeEventListener('focus', hold)
          range.removeEventListener('blur', release)
          el.removeEventListener('mouseenter', hold)
          el.removeEventListener('mouseleave', release)
        })
        return
      }
      if (kind === 'progress') {
        seen.add(el)
        const axis = el.getAttribute('data-axis') || 'x'
        let fill = el.querySelector('[data-fill]') as HTMLElement | null
        if (!fill) return
        const onColor = el.getAttribute('data-color') || '#003399'
        const setDot = (dot: HTMLElement, on: boolean) => {
          const state = dot as HTMLElement & { __off?: { backgroundColor: string; color: string; borderColor: string }; __on?: boolean; __a?: Animation }
          if (!state.__off) {
            const computed = getComputedStyle(dot)
            state.__off = { backgroundColor: computed.backgroundColor, color: computed.color, borderColor: computed.borderTopColor }
            state.__on = false
          }
          if (state.__on === on) return
          state.__on = on
          state.__a?.cancel()
          const onStyle = { backgroundColor: onColor, color: '#FFFFFF', borderColor: onColor }
          state.__a = dot.animate(on ? [state.__off, onStyle] : [onStyle, state.__off], { duration: reduce ? 0 : 280, fill: 'forwards', easing: 'ease-out' })
        }
        const update = () => {
          if (!el.isConnected || !fill) return
          const next = el.querySelector('[data-fill]') as HTMLElement | null
          if (next) fill = next
          const rect = el.getBoundingClientRect()
          const ratio = reduce ? 1 : Math.max(0, Math.min(1, (window.innerHeight * 0.85 - rect.top) / (rect.height + window.innerHeight * 0.3)))
          fill.style.transformOrigin = axis === 'x' ? '0 50%' : '50% 0'
          fill.style.transform = `${axis === 'x' ? 'scaleX' : 'scaleY'}(${ratio})`
          const dots = el.querySelectorAll('[data-dot]')
          dots.forEach((dot, index) => setDot(dot as HTMLElement, ratio > 0.01 && ratio >= (index + 0.15) / dots.length))
        }
        root.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update)
        update()
        stops.push(() => {
          root.removeEventListener('scroll', update)
          window.removeEventListener('resize', update)
        })
        return
      }
      if (reduce) return
      if (kind === 'bob') {
        seen.add(el)
        el.style.willChange = 'transform'
        el.animate(
          [{ transform: 'translateY(0)' }, { transform: 'translateY(-10px)' }, { transform: 'translateY(0)' }],
          { duration: 5200, iterations: Infinity, easing: 'ease-in-out' }
        )
        return
      }
      if (kind === 'swap') {
        seen.add(el)
        const gapValue = Number(el.getAttribute('data-gap'))
        const gap = Math.min(Number.isFinite(gapValue) && gapValue ? gapValue : 260, 260)
        const rows = Array.from(el.children) as HTMLElement[]
        whenVisible(el, () => {
          rows.forEach((row, index) => {
            const worry = row.querySelector('[data-w]') as HTMLElement | null
            const solve = row.querySelector('[data-s]') as HTMLElement | null
            const delay = 120 + index * gap
            const options = { duration: 380, delay, fill: 'forwards' as FillMode, easing: EASE }
            if (worry) worry.animate([{}, { color: '#8B95A1', textDecorationColor: '#8B95A1' }], options)
            if (solve) {
              solve.animate(
                [{ opacity: 0.55, transform: 'translateX(-8px)' }, { opacity: 1, transform: 'none' }],
                { ...options, delay: delay + 80 }
              )
            }
          })
        })
        return
      }
      if (kind !== 'line' && kind !== 'reveal' && kind !== 'fade' && kind !== 'stagger') return
      seen.add(el)
      const delay = delayOf(el)

      if (kind === 'stagger') {
        const animName = el.getAttribute('data-anim')
        const gapAttr = el.getAttribute('data-gap')
        const parentDelay = Number(el.getAttribute('data-delay'))
        const extra = Number.isFinite(parentDelay) ? parentDelay : 0
        const frames: Keyframe[] = animName === 'pop' ? [{ transform: 'scale(0)', opacity: 0 }, { transform: 'none', opacity: 1 }] : UP
        const duration = animName === 'pop' ? 360 : 420
        const anims = Array.from(el.children).map((child, index) => {
          const step = gapAttr ? index * Number(gapAttr) : Math.min(index, 8) * 50
          const target = (animName && (child as HTMLElement).querySelector('[data-kid]')) || child
          return enter(target as HTMLElement, frames, duration, Math.min(step, 360) + extra)
        })
        whenVisible(el, () => anims.forEach((animation) => animation.play()))
        return
      }

      const frames: Keyframe[] =
        kind === 'line' ? [{ transform: 'translateY(108%)' }, { transform: 'none' }] : kind === 'fade' ? [{ opacity: 0 }, { opacity: 1 }] : UP
      const animation = enter(el, frames, kind === 'line' ? 520 : 420, delay)
      whenVisible(el, () => animation.play())
    }

    const scan = () => {
      root.querySelectorAll<HTMLElement>('[data-motion]').forEach(setup)
    }

    scan()
    const observer = new MutationObserver(scan)
    observer.observe(root, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      stops.forEach((stop) => stop())
    }
  }, [])
}
